import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ASSISTANT_PROMPTS, ASSISTANT_CONTEXT } from "@/data/dashboardDemo";
import { Sparkles, Send, Loader2 } from "lucide-react";

export function AiAssistantCard() {
  const { lang } = useI18n();
  const [input, setInput] = useState("");
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const prompts = ASSISTANT_PROMPTS[lang] || ASSISTANT_PROMPTS.fr;

  const ask = async (question) => {
    const q = (question || input).trim();
    if (!q || loading) return;
    setLoading(true);
    setReply("");
    try {
      const { data } = await api.post("/advisor/assistant", {
        prompt: q,
        context: ASSISTANT_CONTEXT[lang] || ASSISTANT_CONTEXT.fr,
        lang,
      });
      setReply(data.reply || "");
    } catch (err) {
      setReply(formatApiError(err.response?.data?.detail) || (lang === "en" ? "Assistant unavailable." : "Assistant indisponible."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-lg border border-[#EAB308]/25 bg-gradient-to-br from-[#EAB308]/[0.06] to-transparent p-6" data-testid="ai-assistant-card">
      <div className="flex items-center gap-2.5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#EAB308]/12 border border-[#EAB308]/30">
          <Sparkles className="w-5 h-5 text-[#EAB308]" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-white">Assistant PROGÈRE AI</h2>
          <p className="text-xs text-white/45">{lang === "en" ? "Ask anything about your day" : "Posez une question sur votre journée"}</p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {prompts.map((p, i) => (
          <button
            key={p}
            data-testid={`ai-prompt-${i}`}
            onClick={() => ask(p)}
            disabled={loading}
            className="rounded-full border border-white/12 bg-white/5 px-3 py-1.5 text-xs text-white/75 hover:border-[#EAB308]/40 hover:text-white transition-colors disabled:opacity-50"
          >
            {p}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {(loading || reply) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 rounded-lg border border-white/10 bg-black/40 p-4 text-sm text-white/85 whitespace-pre-wrap"
            data-testid="ai-assistant-reply"
          >
            {loading ? (
              <span className="flex items-center gap-2 text-white/60"><Loader2 className="w-4 h-4 animate-spin" /> {lang === "en" ? "Thinking…" : "Analyse en cours…"}</span>
            ) : reply}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-4 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && ask()}
          data-testid="ai-assistant-input"
          placeholder={lang === "en" ? "Type your question…" : "Écrivez votre question…"}
          className="flex-1 rounded-md border border-white/12 bg-black/40 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#EAB308]/40 focus:outline-none"
        />
        <button
          onClick={() => ask()}
          disabled={loading || !input.trim()}
          data-testid="ai-assistant-send"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[#EAB308] text-black hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
